import React from 'react';
import { useLocation } from 'react-router-dom';
import { ShieldCheck, Users, TrendingDown } from 'lucide-react';
import ProductPage from '../components/seo/ProductPage';
import { isEnglishPath } from '../utils/i18nRoutes';

const ALTERNATES = [
  { hreflang: 'es', href: '/pando' },
  { hreflang: 'en', href: '/en/pando' },
  { hreflang: 'x-default', href: '/pando' },
];

const CONTENT = {
  es: {
    seoTitle: 'Pando | Copiloto de IA interno de Valoriza',
    seoDescription: 'Copiloto de IA generativa para +500 personas en Valoriza. Conectado a sistemas internos, con gobierno, trazabilidad y cumplimiento del AI Act.',
    breadcrumbItems: [{ label: 'Inicio', to: '/' }, { label: 'Tecnología', to: '/#proyectos' }, { label: 'Pando' }],
    tag: 'Copiloto de IA',
    titleMain: 'Pando: IA de empresa, no un',
    titleAccent: 'ChatGPT abierto',
    lead: 'Pando es el copiloto de IA generativa de Valoriza. Lo usan más de 500 personas. No es un ChatGPT abierto pegado a la intranet: está conectado a sistemas internos, con gobierno, trazabilidad y criterio de cumplimiento del AI Act.',
    problemaLabel: 'El problema',
    problema: 'La información de una empresa de servicios urbanos está en contratos, procedimientos, ERPs, buzones y cabezas. Buscarla mata horas. Pegar esa información en un chatbot público es un riesgo de datos, de alucinación y de AI Act.',
    comoFuncionaLabel: 'Qué hace',
    comoFunciona: 'Responde con el contexto interno de Valoriza (sistemas y documentación a los que está conectado). Deja rastro: quién preguntó, qué modelo, qué coste. Encaja en un gobierno de IA (NeuralTrust como pieza de gobierno). Está pensado para operación diaria, no para un piloto de 12 personas.',
    cifrasLabel: 'Cifras',
    cifras: [
      { icon: Users, value: '+500', label: 'Personas usuarias' },
      { icon: TrendingDown, value: '~30%', label: 'Menos tiempo buscando info' },
      { icon: ShieldCheck, value: '2026', label: 'Política y Comité de IA' },
    ],
    paraQuienLabel: 'Para quién',
    paraQuien: 'Mandos intermedios, back office, innovación, prevención, contratos: cualquiera que hoy pierde la mañana buscando "dónde está el procedimiento".',
    enlacesLabel: 'Enlaces',
    enlaces: [
      { label: 'TrisTrash — la calle como sensor', to: '/tristrash' },
      { label: 'Quién lidera el sistema', to: '/equipo/david-redondo-perez' },
    ],
    ctaText: '¿Quieres un copiloto con gobierno?',
  },
  en: {
    seoTitle: 'Pando | Valoriza\u2019s internal AI copilot',
    seoDescription: 'A generative AI copilot for +500 people at Valoriza. Connected to internal systems, with governance, traceability and AI Act compliance.',
    breadcrumbItems: [{ label: 'Home', to: '/en' }, { label: 'Technology', to: '/en#proyectos' }, { label: 'Pando' }],
    tag: 'AI copilot',
    titleMain: 'Pando: enterprise AI, not an',
    titleAccent: 'open ChatGPT',
    lead: 'Pando is Valoriza\u2019s generative AI copilot, used by more than 500 people. It\u2019s not an open ChatGPT bolted onto the intranet: it\u2019s connected to internal systems, with governance, traceability and an AI Act-compliant design.',
    problemaLabel: 'The problem',
    problema: 'Information at an urban services company lives in contracts, procedures, ERPs, inboxes and people\u2019s heads. Finding it kills hours. Pasting that information into a public chatbot is a data, hallucination and AI Act risk.',
    comoFuncionaLabel: 'What it does',
    comoFunciona: 'Answers using Valoriza\u2019s internal context (the systems and documentation it\u2019s connected to). Leaves a trail: who asked, which model, what it cost. Fits into an AI governance framework (NeuralTrust as the governance layer). Built for daily operation, not a 12-person pilot.',
    cifrasLabel: 'Numbers',
    cifras: [
      { icon: Users, value: '+500', label: 'Users' },
      { icon: TrendingDown, value: '~30%', label: 'Less time searching for info' },
      { icon: ShieldCheck, value: '2026', label: 'AI Policy & Committee' },
    ],
    paraQuienLabel: 'Who it\u2019s for',
    paraQuien: 'Middle managers, back office, innovation, prevention, contracts teams: anyone who currently loses a morning looking for "where is the procedure".',
    enlacesLabel: 'Links',
    enlaces: [
      { label: 'TrisTrash — the street as a sensor', to: '/en/tristrash' },
      { label: 'Who leads the system', to: '/en/team/david-redondo-perez' },
    ],
    ctaText: 'Want a governed copilot?',
  },
};

export default function PandoPage() {
  const { pathname } = useLocation();
  const locale = isEnglishPath(pathname) ? 'en' : 'es';
  const c = CONTENT[locale];

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Pando',
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    url: `https://innovation.valorizasm.com${locale === 'en' ? '/en/pando' : '/pando'}`,
    description: c.seoDescription,
    publisher: { '@id': 'https://innovation.valorizasm.com/#organization' },
  };

  return (
    <ProductPage
      testId="pando-page"
      path={locale === 'en' ? '/en/pando' : '/pando'}
      locale={locale}
      alternates={ALTERNATES}
      jsonLd={[jsonLd]}
      {...c}
    />
  );
}
